import React, { ReactNode, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { DT, getPersistentSessionKey } from "../../../utils/commonfunction";
import { readReviewDraft, saveDraftGuestCount, TableDetails } from "../utils/qrVisit";

interface QrOrderState {
  QrOrderToken?: { tableDetails: TableDetails | null };
}

const QUICK_ADD = [1, 2, 5, 10];

interface GuestCountGateProps {
  children: ReactNode;
  /**
   * Cashier binding (WRDS-1202). Passing `asking` puts the gate in CONTROLLED
   * mode: the caller decides when to ask, what the ceiling is and where the
   * answer goes, and none of the QR visit-draft machinery below runs — the
   * cashier holds its count in memory for one order instead of persisting a
   * draft for the length of a customer's visit.
   *
   * Omit all three and the QR behaviour is exactly as before.
   */
  capacity?: number;
  asking?: boolean;
  onConfirm?: (count: number) => void;
}

const GuestCountGate = ({
  children,
  capacity: capacityProp,
  asking: askingProp,
  onConfirm,
}: GuestCountGateProps) => {
  const { t } = useTranslation();
  const queryParams = new URLSearchParams(window.location.search);
  const storedContext = (() => {
    try {
      const saved = getPersistentSessionKey("qr_context");
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  })();
  const restaurant_id = queryParams.get("restaurant_id") || storedContext?.restaurant_id || null;
  const table_id = queryParams.get("table_id") || storedContext?.table_id || null;
  // Hooks cannot be conditional, so this still runs for a controlled (cashier)
  // caller whose store has no QR slice at all — read through it defensively
  // rather than reaching into a slice that surface never registers.
  const tableDetails = useSelector(
    (state: QrOrderState) => state?.QrOrderToken?.tableDetails ?? null
  );
  const [typed, setTyped] = useState("1");
  const [, setConfirmed] = useState(false);

  const controlled = askingProp !== undefined;

  // QR mode cannot render before the table loads; a controlled caller has
  // already resolved its own table and passes the answer in.
  if (!controlled && !tableDetails) {
    return null;
  }

  const capacity = controlled
    ? Number(capacityProp) || 0
    : Number(tableDetails?.capacity) || 0;

  const asking = controlled
    ? askingProp
    : !(
        capacity <= 1 ||
        tableDetails?.activeOrder ||
        readReviewDraft(restaurant_id, table_id)?.number_of_persons
      );

  const count = Math.min(Number(typed) || 0, capacity);
  const setClamped = (next: number) =>
    setTyped(String(Math.min(Math.max(next, 1), capacity)));
  const capped = (raw: string) => {
    const digits = raw.replace(/\D/g, "");
    return digits && Number(digits) > capacity ? String(capacity) : digits;
  };

  const inertWhileAsking: Record<string, string> = asking ? { inert: "" } : {};

  const confirm = () => {
    if (controlled) {
      onConfirm?.(count);
      return;
    }
    saveDraftGuestCount(restaurant_id, table_id, count);
    // Forces the re-read of the draft above, which is what hides the gate.
    setConfirmed(true);
  };

  return (
    <div className={asking ? "qr-guest-gate-root" : "qr-guest-gate-passthrough"}>
      <div
        className={asking ? "qr-guest-gate-menu" : "qr-guest-gate-passthrough"}
        aria-hidden={asking || undefined}
        {...inertWhileAsking}
      >
        {children}
      </div>
      {asking && (
        <div className="qr-body-wrap qr-guest-count-wrap">
          <div className="qr-guest-gate-card">
            <p className="qr-guest-gate-question">{t("guest_gate_question")}</p>

            <div className="qr-guest-gate-stepper">
              <button
                type="button"
                aria-label={t("guest_gate_decrease")}
                disabled={count <= 1}
                onClick={() => setClamped(count - 1)}
              >
                −
              </button>
              <input
                className="qr-guest-gate-value"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={typed}
                aria-label={t("guest_gate_question")}
                onChange={(event) => setTyped(capped(event.target.value))}
              />
              <button
                type="button"
                aria-label={t("guest_gate_increase")}
                disabled={count >= capacity}
                onClick={() => setClamped(count + 1)}
              >
                +
              </button>
            </div>
            <button
              type="button"
              className="qr-guest-gate-reset"
              disabled={count === 1}
              onClick={() => setTyped("1")}
            >
              ↺ {t("guest_gate_reset")}
            </button>

            <p className="qr-guest-gate-quick-label">{t("guest_gate_quick_add")}:</p>
            <div className="qr-guest-gate-quick">
              {QUICK_ADD.map((step) => (
                <button
                  type="button"
                  key={step}
                  disabled={count + step > capacity}
                  onClick={() => setClamped(count + step)}
                >
                  +{step}
                </button>
              ))}
            </div>
            <p className="qr-guest-gate-capacity">
              {DT(t("guest_gate_max_capacity"), [capacity])}
            </p>

            <button
              type="button"
              className="qr-guest-gate-confirm"
              disabled={count < 1}
              onClick={confirm}
            >
              {t("guest_gate_confirm")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GuestCountGate;
